import type { BookingState } from '../../api/types';
import { type DateFormats, formatDate, formatDateTime } from '../../lib/dateFormat';
import { ourRef } from '../../lib/reservationRef';

/**
 * How a dispatch booking reads wherever the console shows one — the contact
 * panel's card, the mail reading view's last-reservation strip and the chip on
 * a mail list row. One palette and one pickup formatter, so a ride that is
 * "Assigned" in blue on the panel is not orange on the row beside it.
 */
export const BOOKING_TONE: Record<BookingState, { bg: string; fg: string }> = {
  upcoming: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)' },
  active: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)' },
  completed: { bg: 'var(--c-secondary)', fg: 'var(--c-muted)' },
  cancelled: { bg: 'var(--c-red-muted)', fg: 'var(--c-red)' },
  quote: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)' },
};

export const bookingTone = (state: BookingState) => BOOKING_TONE[state] ?? BOOKING_TONE.upcoming;

const pickupDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The pickup, in the ride's own zone and the tenant's own shape: "02/09/2026,
 * 06:40 pm". "Date TBC" when dispatch has none.
 *
 * The tenant's `formats` come from `useTimeFormat`, but the ZONE does not: a
 * pickup carries the zone of the place the car turns up, which is only
 * sometimes the dispatch clock, so this one value renders in `rideZone` while
 * every other timestamp on screen uses the shared binding.
 */
export function pickupLabel(iso: string | null, rideZone: string | null, formats: DateFormats): string {
  const date = pickupDate(iso);
  return date ? formatDateTime(date, rideZone, formats) : 'Date TBC';
}

/** Just the pickup's calendar day, same zone rule — what fits on a list row. */
export function pickupDay(iso: string | null, rideZone: string | null, formats: DateFormats): string | null {
  const date = pickupDate(iso);
  return date ? formatDate(date, rideZone, formats.dateFormat) : null;
}

/** A reservation's own number — "Ref #48213" — as the chip, the strip and the card print it. */
export const bookingRef = (booking: { kind: 'quote' | 'job'; reference: string }) =>
  ourRef(booking.kind, booking.reference);
